import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api.js';
import { t, wanneer, geleden, huidigeTaal } from '../i18n.js';
import { St, ERNST } from '../components/Status.jsx';
import Vlag from '../components/Vlag.jsx';
import Lijsten from '../components/Lijsten.jsx';
import { landnaam } from '../components/Wereldkaart.jsx';
import { blokStand } from './Blokkades.jsx';

/**
 * Eén blokkade (docs/besluiten/blokkades-bouwen.md): wat ZERO gaat doen (per firewall het object en de regel, live van
 * de FMC), beoordelen met de vervaltijd, of opheffen; en wat er over het adres bekend is.
 */
const ttlKeuzes = () => [[1, t('1 uur')], [4, t('4 uur')], [24, t('24 uur')], [72, t('3 dagen')], [168, t('7 dagen')], [720, t('30 dagen')]];
const doelStand = (s) => ({ gezet: ['good', t('staat erin')], verwijderd: ['neutral', t('weggehaald')], mislukt: ['critical', t('mislukt')],
  overgeslagen: ['warning', t('overgeslagen')], wacht: ['neutral', t('wacht')] }[s] ?? ['neutral', s]);
const wieNaam = (w) => w?.name || w?.number || 'ADTS ZERO';

export default function Blokkade({ mij }) {
  const { nummer } = useParams();
  const client = useQueryClient();
  const q = useQuery({ queryKey: ['blokkades', 'een', nummer], queryFn: () => api.blokkade(nummer), refetchInterval: 30_000 });
  const wacht = q.data?.data.status === 'wacht';
  const plan = useQuery({ queryKey: ['blokkades', 'plan', nummer], queryFn: () => api.blokkadePlan(nummer), enabled: wacht, refetchOnWindowFocus: false });
  const [ttl, zetTtl] = useState(null);
  const [reden, zetReden] = useState('');
  const [afwijzen, zetAfwijzen] = useState(false);
  const [melding, zetMelding] = useState(null);
  const [bezig, zetBezig] = useState(false);
  const mag = (r) => mij.permissions.includes(r);
  if (q.isPending) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) return <div className="melder rood">{t(q.error.message)}</div>;
  const b = q.data.data;
  const doe = async (fn) => {
    zetMelding(null); zetBezig(true);
    try { await fn(); } catch (e) { zetMelding(t(e.details ? Object.values(e.details)[0] : e.message)); } finally {
      zetBezig(false); client.invalidateQueries({ queryKey: ['blokkades'] });
    }
  };
  const taal = huidigeTaal();
  const a = b.about;
  return (
    <>
      <Link className="crumb" to="/blokkades">← {t('Blokkades')}</Link>
      <div className="page-head"><div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
          <St soort={blokStand(b.status)[0]}>{blokStand(b.status)[1]}</St> <span className="muted mono">{b.number}</span>
          <span className="muted">· {t('voorgesteld door {wie} {wanneer}', { wie: wieNaam(b.proposedBy), wanneer: geleden(b.proposedAt) })}</span></div>
        <h1 style={{ fontFamily: "var(--mono)" }}>{b.ip}</h1>
        <p className="sub"><b>{b.tenant.name}</b> · {b.reason}{b.alert && <> · {t('naar aanleiding van')} <Link to={`/alerts/${b.alert.number}`}>{b.alert.number}</Link></>}</p>
      </div></div>
      {melding && <div className="melder rood" style={{ marginBottom: 14 }}>{melding}</div>}
      {b.error && b.status !== 'afgewezen' && <div className="callout warn" style={{ marginBottom: 14 }}>{t('De laatste poging lukte niet:')} {b.error}</div>}

      <div className="grid-2">
        <div className="stack">
          <section className="card">
            <header><h2>{wacht ? t('Wat ADTS ZERO gaat doen') : t('Waar hij staat')}</h2><span className="meta">{t('met de blokkeergebruiker van de FMC, zonder deploy')}</span></header>
            {wacht ? (plan.isPending ? <p className="muted">{t('De FMC vragen…')}</p> : plan.isError ? <div className="melder rood">{t(plan.error.message)}</div> : (
              <div className="plan">{plan.data.data.firewalls.length ? plan.data.data.firewalls.map((f) => (
                <div key={f.id} className="step">
                  <div className="h"><St soort={f.status === 'klaar' ? 'good' : f.status === 'mislukt' ? 'critical' : 'warning'} />{f.name} <span className="muted">· {f.fmc}</span></div>
                  <div className="soft" style={{ fontSize: 13 }}>
                    {f.status === 'klaar' ? <>{t('Adres in')} <span className="mono">{f.object}</span>{t(', dat in het toegangsbeleid {beleid} staat:', { beleid: f.rule.beleid })} {[f.rule.van && t('inkomend ({regel})', { regel: f.rule.van }), f.rule.naar && t('uitgaand ({regel})', { regel: f.rule.naar })].filter(Boolean).join(', ')}. {t('Geldt meteen.')}</>
                      : f.status === 'mislukt' ? f.error
                      : <>{f.error ?? (!f.objectExists ? t('Het object {object} bestaat nog niet; ADTS ZERO maakt het bij goedkeuren.', { object: f.object }) : '')} {t('Er staat nog geen blokkeerregel met {object} in het toegangsbeleid: dan slaat ADTS ZERO deze firewall over. Zie Bronnen › {fw} › Blokkeren.', { object: f.object, fw: f.name })}</>}
                  </div>
                </div>
              )) : <p className="empty">{t('Deze klant heeft geen firewall aan internet onder een FMC.')}</p>}</div>
            )) : b.targets.length ? (
              <div className="rows">{b.targets.map((d) => (
                <div key={d.sourceId} className="row" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}>
                  <span><b>{d.name}</b> <span className="muted mono" style={{ fontSize: 12 }}>{d.object}</span>{d.error && <><br /><span className="muted" style={{ fontSize: 12.5 }}>{d.error}</span></>}</span>
                  <St soort={doelStand(d.status)[0]}>{doelStand(d.status)[1]}</St>
                </div>))}</div>
            ) : <p className="empty">{t('Niets op een firewall gezet.')}</p>}
          </section>

          {wacht && (
            <section className="card">
              <header><h2>{t('Beoordelen')}</h2></header>
              {mag('blokkades.goedkeuren') ? (afwijzen ? (
                <form className="form" onSubmit={(e) => { e.preventDefault(); doe(() => api.wijsBlokkadeAf(b.number, reden)); }}>
                  <p className="soft" style={{ margin: 0 }}>{t('Er wordt niets op een firewall gewijzigd. Schrijf op waarom, zodat een collega het later begrijpt.')}</p>
                  <textarea value={reden} onChange={(e) => zetReden(e.target.value)} placeholder={t('Bijvoorbeeld: adres is van een leverancier met een storing.')} />
                  <div className="actions" style={{ padding: 0, justifyContent: 'flex-start' }}>
                    <button className="btn danger" disabled={bezig || reden.trim().length < 3}>{t('Afwijzen')}</button>
                    <button type="button" className="btn" onClick={() => zetAfwijzen(false)}>{t('Annuleren')}</button></div>
                </form>
              ) : (
                <div className="stack" style={{ gap: 12 }}>
                  <div className="field"><span className="label">{t('Vervaltijd')}</span>
                    <div className="kinds" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>{ttlKeuzes().map(([u, l]) => (
                      <label key={u}><span><input type="radio" name="ttl" checked={(ttl ?? b.ttlHours) === u} onChange={() => zetTtl(u)} />{l}</span>
                        {u === b.ttlHours && <small>{u === 168 ? t('Voorgesteld: het adres was eerder geblokkeerd') : t('Voorgesteld')}</small>}</label>))}</div></div>
                  <div className="actions" style={{ padding: 0, justifyContent: 'flex-start' }}>
                    <button className="btn primary" disabled={bezig} onClick={() => doe(() => api.keurBlokkadeGoed(b.number, ttl ?? b.ttlHours))}>{bezig ? t('Bezig…') : t('Goedkeuren en uitvoeren')}</button>
                    <button type="button" className="btn danger" onClick={() => zetAfwijzen(true)}>{t('Afwijzen')}</button></div>
                </div>
              )) : <p className="muted">{t('Goedkeuren of afwijzen vraagt het recht Blokkades goedkeuren.')}</p>}
            </section>
          )}
          {b.status === 'actief' && (
            <section className="card">
              <header><h2>{t('Actief')}</h2></header>
              <p>{t('Goedgekeurd door {wie} om {wanneer}. Vervalt {vervalt}; dan haalt ADTS ZERO het adres zelf weg.', { wie: wieNaam(b.decidedBy), wanneer: wanneer(b.decidedAt), vervalt: wanneer(b.expiresAt) })}</p>
              {mag('blokkades.goedkeuren') && <div className="actions" style={{ justifyContent: 'flex-start' }}>
                <button className="btn" disabled={bezig} onClick={() => doe(() => api.hefBlokkadeOp(b.number))}>{bezig ? t('Bezig…') : t('Nu opheffen')}</button></div>}
              <p className="noot">{t('Opheffen vraagt geen goedkeuring: terug naar de oude toestand is altijd veilig.')}</p>
            </section>
          )}
          {!wacht && b.status !== 'actief' && (
            <section className="card"><header><h2>{t('Afgehandeld')}</h2></header>
              <p>{b.status === 'afgewezen' ? t('Afgewezen door {wie} om {wanneer}: {waarom}', { wie: wieNaam(b.decidedBy), wanneer: wanneer(b.decidedAt), waarom: b.decisionNote })
                : b.status === 'opgeheven' ? t('Opgeheven door {wie} om {wanneer}.', { wie: wieNaam(b.liftedBy), wanneer: wanneer(b.liftedAt) })
                : b.status === 'verlopen' ? t('Verlopen om {wanneer}; ADTS ZERO heeft het adres weggehaald.', { wanneer: wanneer(b.liftedAt) }) : t('Mislukt.')}</p></section>
          )}
        </div>
        <div className="stack">
          <section className="card">
            <header><h2>{t('Over dit adres')}</h2></header>
            <dl className="facts">
              <dt>{t('Herkomst')}</dt><dd>{a.cc ? <><Vlag cc={a.cc} />{[landnaam(a.cc, taal), a.city].filter(Boolean).join(', ')}</> : '—'}{a.asn && <><br /><span className="muted"><span className="mono">AS{a.asn}</span> {a.org}</span></>}</dd>
              <dt>{t('Threat intel')}</dt><dd>{a.lists.length ? <Lijsten lijsten={a.lists} /> : <span className="muted">{t('op geen lijst')}</span>}</dd>
              <dt>{t('Eerder geblokkeerd')}</dt><dd>{a.earlier.length ? a.earlier.map((x) => <div key={x.number}><Link to={`/blokkades/${x.number}`} className="mono">{x.number}</Link> {blokStand(x.status)[1]} · {x.tenant}</div>) : t('nee')}</dd>
              <dt>{t('Alerts')}</dt><dd>{a.alerts.length ? a.alerts.map((x) => <div key={x.number}><St soort={x.severity}>{t(ERNST[x.severity])}</St> <Link to={`/alerts/${x.number}`} className="mono">{x.number}</Link> <span className="muted">{x.tenant}</span></div>) : '—'}</dd>
            </dl>
          </section>
          {b.notifications.length > 0 && (
            <section className="card">
              <header><h2>{t('Melding')}</h2></header>
              <div className="rows">{b.notifications.map((n, i) => (
                <div key={i} className="row" style={{ gridTemplateColumns: '60px minmax(0,1fr)' }}>
                  <span className="tag">Webex</span>
                  <span className="soft">{n.status === 'verstuurd' ? t('Verstuurd {wanneer}', { wanneer: wanneer(n.sentAt) })
                    : n.status === 'wacht' ? (n.holdUntil ? t('Wacht tot {wanneer} (buiten kantooruren)', { wanneer: wanneer(n.holdUntil) }) : t('Wordt zo verstuurd'))
                    : n.status === 'uit' ? t('Niet verstuurd: {reden}', { reden: t(n.error ?? '') }) : t('Mislukt: {reden}', { reden: n.error ?? '' })}</span>
                </div>))}</div>
            </section>
          )}
          <div className="callout">{t('Een blokkade houdt nieuwe verbindingen tegen. Een sessie die al loopt, moet je zelf beëindigen.')}</div>
        </div>
      </div>
    </>
  );
}
